import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { FileStatus, FileUploadInput, UploadedFile } from './types';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useUploadToStorage } from './useUploadToStorage';

type FileUploadRequest = {
  fileName?: string;
  fileSize: number;
  fileType: string;
  isFolder: boolean;
  isResolved: boolean;
  isTemporary: boolean;
  modelId?: string;
  parentId?: string;
  documentDate?: string | null;
  location?: string;
  uploadToken?: string;
};

export const useFileUploadActions = ({
  clientId: overrideClientId,
}: { clientId?: string } = {}) => {
  const { clientId: defaultClientId } = useCurrentClientIfAvailable();
  const clientId = overrideClientId ?? defaultClientId;
  const queryClient = useQueryClient();
  const { uploadToStorage, extractMetadata } = useUploadToStorage();
  const resource = `clients/${clientId}/data-upload-files`;
  const fileListKey = ['clients', clientId, 'data-upload-files'];

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, string>({
    mutationFn: (fileId: string) => api.delete(`${resource}/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileListKey });
    },
  });

  const { mutateAsync: uploadFiles, error: uploadError } = useMutation<
    { data: UploadedFile }[],
    Error,
    FileUploadInput[]
  >({
    mutationFn: (files: FileUploadInput[]) =>
      Promise.all(
        files.map(async fileInput => {
          const requestInput: FileUploadRequest = {
            fileName: fileInput.fileName || fileInput.file?.name,
            fileSize: fileInput.file?.size || 0,
            fileType: fileInput.file?.type || 'FOLDER',
            isFolder: fileInput.isFolder || false,
            isResolved: fileInput.isResolved || false,
            isTemporary: fileInput.isTemporary || false,
            modelId: fileInput.modelId || undefined,
            parentId: fileInput.parentId,
            documentDate: fileInput.documentDate?.split('T')[0],
          };
          if (fileInput.file) {
            const uploadResult = await uploadToStorage(fileInput.file, clientId);
            if (!requestInput.documentDate) {
              const metadata = await extractMetadata(
                uploadResult.location,
                fileInput.file,
                uploadResult.uploadToken,
              );
              if (metadata.creationDate) {
                requestInput.documentDate = metadata.creationDate;
              }
            }
            if (uploadResult.location) {
              requestInput.location = uploadResult.location;
              requestInput.uploadToken = uploadResult.uploadToken;
            }
          }

          return api.post<UploadedFile>(resource, requestInput);
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileListKey });
    },
  });

  const deleteFile = async (fileId: string) => {
    await deleteFileMutation(fileId);
  };

  const setFileResolved = async (file: UploadedFile, isResolved: boolean) => {
    await api.put(`${resource}/${file.id}`, {
      isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: null,
    });
    queryClient.invalidateQueries({ queryKey: fileListKey });
  };

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    await api.put(`${resource}/${file.id}`, {
      parentId: parentId ?? null,
      isResolved: file.isResolved,
      isFiled: true,
      fileName: null,
    });
    queryClient.invalidateQueries({ queryKey: fileListKey });
  };

  const createFolder = async (name: string, parentId?: string) => {
    await api.post(resource, {
      fileName: name,
      fileSize: 0,
      fileType: 'FOLDER',
      isFolder: true,
      isResolved: true,
      parentId,
    });
    queryClient.invalidateQueries({ queryKey: fileListKey });
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    await api.put(`${resource}/${file.id}`, {
      isResolved: file.isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: newName,
    });
    queryClient.invalidateQueries({ queryKey: fileListKey });
  };

  const getFileStatus = (file: UploadedFile): FileStatus => {
    const status = file.backgroundProcess?.status;
    if (status === 'SUCCESS' || !status) {
      return { color: 'green.6', label: 'Ready' };
    } else if (status === 'FAILED') {
      return { color: 'red.6', label: 'Failed' };
    } else {
      return {
        color: 'blue.6',
        label: 'Processing',
        showLoader: true,
      };
    }
  };

  return {
    uploadFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    setFileResolved,
    moveFile,
    createFolder,
    renameFolder,
    getFileStatus,
    error: uploadError,
  };
};
